import {
  Button,
  ButtonColor,
  DateFormat,
  formatDate,
  formatName,
  pluralize,
  ProfileImage,
  Rating,
  RatingSize,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import React, { useState } from 'react';

import { categoryUrnToSlug } from '../../utils/category';
import {
  getBucketCustomersCoached,
  getCoachUrl,
  getExperienceLevels,
} from '../../utils/coach';
import { useCountLines } from '../../utils/hooks/useCountLines';
import { Svg } from '../SvgIcon';
import Qualification from '../common/Qualification';

import type {
  OfferingPackageCoachAboutCategoryFragment,
  OfferingPackageCoachAboutCoachFragment,
} from './__generated-gql-types__/OfferingPackageCoachAbout.generated';

interface OfferingPackageCoachAboutProps {
  coach: OfferingPackageCoachAboutCoachFragment;
  category: OfferingPackageCoachAboutCategoryFragment;
  viewAllReviews: () => void;
}

const OfferingPackageCoachAbout: React.FC<OfferingPackageCoachAboutProps> = ({
  coach,
  category,
  viewAllReviews,
}) => {
  const [coachQualificationsLines, coachQualificationsRef] = useCountLines();

  const [expandedCoachQualifications, setExpandedCoachQualifications] =
    useState(false);

  const selectedCategory = coach.categoryInformationList?.find(
    (categoryInfo) => categoryInfo.category.id == category?.id,
  );
  const headline = selectedCategory?.headline;
  const qualifications = selectedCategory?.qualifications;

  const bucketedCustomersCoached = selectedCategory?.customersCoached
    ? getBucketCustomersCoached(selectedCategory.customersCoached)
    : null;

  return (
    <div className="grid gap-6 md:grid-cols-2 md:gap-14">
      <div className="flex flex-col items-start justify-center">
        <div className="flex items-center justify-start space-x-4">
          <ProfileImage className="size-12" {...coach.user} />
          <div className="flex flex-col items-start justify-center">
            <h3 className="text-lg font-medium text-leland-gray-dark">
              Offered by{' '}
              {formatName({
                firstName: coach.user.firstName,
                lastName: coach.user.lastName,
              })}
            </h3>
            <p className="text-lg font-normal leading-6 text-leland-gray-light">
              Joined {formatDate(coach.createdAt, DateFormat.LONG_MONTH_YEAR)}
            </p>
          </div>
        </div>
        {coach.reviewTotal > 0 ? (
          <div className="mt-6 flex items-center justify-start space-x-2">
            <div className="text-leland-gray-dark">
              <Rating
                rate={coach.averageReviewRate}
                showRate
                size={RatingSize.SMALL}
              />
            </div>
            <button
              type="button"
              className="link link--primary"
              onClick={viewAllReviews}
              id="offering_package_coach_about--view_reviews"
            >
              {pluralize(coach.reviewTotal, 'review')}
            </button>
          </div>
        ) : null}
        {headline ? (
          <p className="mt-6 text-lg font-normal text-leland-gray-dark">
            {headline}
          </p>
        ) : null}
        {qualifications ? (
          <>
            <p
              ref={coachQualificationsRef}
              className={`mt-1 whitespace-pre-line break-words text-lg font-normal leading-6 text-leland-gray-light ${
                !expandedCoachQualifications &&
                coachQualificationsLines > 5 &&
                'line-clamp-4'
              }`}
            >
              {qualifications}
            </p>
            {coachQualificationsLines > 5 ? (
              <button
                className="link link--primary mt-0.5 text-base font-medium"
                onClick={() =>
                  setExpandedCoachQualifications((prevState) => !prevState)
                }
                id="offering_package_coach_about--toggle_qualifications"
              >
                {expandedCoachQualifications ? 'Read less' : 'Read more'}
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="flex flex-col items-start justify-start">
        {coach.professionalCoach ? (
          <Qualification
            icon={Svg.CERTIFICATE_CHECKMARK}
            text="Coaches professionally"
          />
        ) : null}
        {selectedCategory?.experienceLevel &&
        getExperienceLevels(selectedCategory.category.id)[
          selectedCategory.experienceLevel
        ] ? (
          <Qualification
            icon={Svg.EMPLOYEE_BADGE}
            text={`Experience level: ${
              getExperienceLevels(selectedCategory.category.id)[
                selectedCategory.experienceLevel
              ]
            }`}
          />
        ) : null}
        {selectedCategory?.testScore && selectedCategory.displayTestScore ? (
          <Qualification
            icon={Svg.TEST}
            text={`Scored a ${selectedCategory.testScore} on the ${selectedCategory.category.name}`}
          />
        ) : null}
        {bucketedCustomersCoached ? (
          <Qualification
            icon={Svg.PEOPLE}
            text={`${bucketedCustomersCoached} people coached for ${selectedCategory?.category.name}`}
          />
        ) : null}
        {coach.openToInternational ? (
          <Qualification
            icon={Svg.EARTH}
            text="Open to working with clients outside the U.S."
          />
        ) : null}
        <div className="mt-6 flex items-center justify-start space-x-2">
          {category ? (
            <Link
              href={getCoachUrl({
                coachSlug: coach.slug,
                categorySlug: categoryUrnToSlug(category.id),
              })}
              target="_blank"
              rel="noopener noreferrer"
              id="offering_package_coach_about--view_coach_profile"
            >
              <Button label="View profile" buttonColor={ButtonColor.WHITE} />
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OfferingPackageCoachAbout;
